import LogConfig from './logConfig';
import config from '../../config';
import { apiHandler } from '../api/responseHandler';

// Flag to prevent cascading errors (logging errors about logging)
let isCurrentlyLogging = false;

const isDevelopment = () => process.env.NODE_ENV === 'development';

const logger = {
    log: async function (category, componentName, message, obj1, obj2) {
        if (!LogConfig.enabled || !LogConfig[category]) return;

        const prefix = componentName ? `[${componentName}]` : '';

        let logDetail = `${prefix}[${category}] ${message}`;

        if (obj1 !== undefined) {
            try {
                logDetail += `\n${JSON.stringify(obj1, null, 2)}`;
            } catch (stringifyErr) {
                logDetail += `\n[Object could not be stringified: ${stringifyErr.message}]`;
            }
        }

        if (obj2 !== undefined) {
            try {
                logDetail += `\n${JSON.stringify(obj2, null, 2)}`;
            } catch (stringifyErr) {
                logDetail += `\n[Object could not be stringified: ${stringifyErr.message}]`;
            }
        }

        // In development, only log to console - skip API calls
        if (isDevelopment()) {
            console.log(logDetail);
            return;
        }

        // In production, try to send to API but fail silently if issues occur
        if (isCurrentlyLogging) {
            // Prevent cascading errors - don't log errors about logging
            console.warn('[Logger] Skipping nested log attempt to prevent cascade');
            return;
        }

        isCurrentlyLogging = true;

        try {
            // Extract userId from obj1 if it exists, otherwise use fallback
            let userId = 0;
            if (obj1 && typeof obj1 === 'object' && obj1.userId !== undefined) {
                userId = obj1.userId;
            }

            // Use correct parameter names that match backend expectations
            const requestBody = {
                issueCode: category,
                userId: userId,
                issueDetail: logDetail
            };

            const response = await apiHandler.fetch(`${config.API_URL}/api/Issue-log`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(requestBody)
            });

            if (response.error) {
                // Silent fail - just log once to console
                console.warn('[Logger] Failed to send log to API (this is expected if endpoint not implemented)');
            }
        } catch (err) {
            // Silent fail in production - don't create console noise
            // Only log once as a warning
            console.warn('[Logger] Log API unavailable (this is normal in development)');
        } finally {
            isCurrentlyLogging = false;
        }
    },

    logError: async function (category, componentName, message, obj1, obj2) {
        if (!LogConfig[category]) return;

        const prefix = componentName ? `[${componentName}]` : '';

        let logDetail = `${prefix}[${category}] ${message}`;

        if (obj1 !== undefined) {
            try {
                logDetail += `\n${JSON.stringify(obj1, null, 2)}`;
            } catch (stringifyErr) {
                logDetail += `\n[Object could not be stringified: ${stringifyErr.message}]`;
            }
        }

        if (obj2 !== undefined) {
            try {
                logDetail += `\n${JSON.stringify(obj2, null, 2)}`;
            } catch (stringifyErr) {
                logDetail += `\n[Object could not be stringified: ${stringifyErr.message}]`;
            }
        }

        // In development, only log to console - skip API calls
        if (isDevelopment()) {
            console.error(logDetail);
            return;
        }

        // In production, try to send to API but fail silently if issues occur
        if (isCurrentlyLogging) {
            // Prevent cascading errors - don't log errors about logging
            console.warn('[Logger] Skipping nested error log to prevent cascade');
            return;
        }

        isCurrentlyLogging = true;

        try {
            // Extract userId from obj1 if it exists, otherwise use fallback
            let userId = 0;
            if (obj1 && typeof obj1 === 'object' && obj1.userId !== undefined) {
                userId = obj1.userId;
            }

            // Use correct parameter names that match backend expectations
            const requestBody = {
                issueCode: category,
                userId: userId,
                issueDetail: logDetail
            };

            const response = await apiHandler.fetch(`${config.API_URL}/api/Error-log`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(requestBody)
            });

            if (response.error) {
                // Silent fail - just log once to console
                console.warn('[Logger] Failed to send error log to API (this is expected if endpoint not implemented)');
            }
        } catch (err) {
            // Silent fail in production - don't create console noise
            // Only log once as a warning
            console.warn('[Logger] Error log API unavailable (this is normal in development)');
        } finally {
            isCurrentlyLogging = false;
        }
    },

    logWithTrace: async function (category, componentName, message, obj1, obj2) {
        if (!LogConfig.enabled || !LogConfig[category]) return;

        const prefix = componentName ? `[${componentName}]` : '';

        // In development, just use console with trace
        if (isDevelopment()) {
            if (obj1 === undefined) {
                console.log(`${prefix}[${category}] ${message}`);
                console.trace();
            } else if (obj2 === undefined) {
                console.log(`${prefix}[${category}] ${message}`, obj1);
                console.trace();
            } else {
                console.log(`${prefix}[${category}] ${message}`, obj1, obj2);
                console.trace();
            }
            return;
        }

        // Production: send to API with stack trace
        const trace = new Error().stack;

        let logDetail = `${prefix}[${category}] ${message}`;

        if (obj1 !== undefined) {
            try {
                logDetail += `\n${JSON.stringify(obj1, null, 2)}`;
            } catch (stringifyErr) {
                logDetail += `\n[Object could not be stringified: ${stringifyErr.message}]`;
            }
        }

        if (obj2 !== undefined) {
            try {
                logDetail += `\n${JSON.stringify(obj2, null, 2)}`;
            } catch (stringifyErr) {
                logDetail += `\n[Object could not be stringified: ${stringifyErr.message}]`;
            }
        }

        logDetail += `\nStack Trace:\n${trace}`;

        if (isCurrentlyLogging) {
            console.warn('[Logger] Skipping nested traced log to prevent cascade');
            return;
        }

        isCurrentlyLogging = true;

        try {
            // Extract userId from obj1 if it exists, otherwise use fallback
            let userId = 0;
            if (obj1 && typeof obj1 === 'object' && obj1.userId !== undefined) {
                userId = obj1.userId;
            }

            // Use correct parameter names that match backend expectations
            const requestBody = {
                issueCode: category,
                userId: userId,
                issueDetail: logDetail
            };

            const response = await apiHandler.fetch(`${config.API_URL}/api/Issue-log`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(requestBody)
            });

            if (response.error) {
                console.warn('[Logger] Failed to send traced log to API');
            }
        } catch (err) {
            console.warn('[Logger] Traced log API unavailable');
        } finally {
            isCurrentlyLogging = false;
        }
    }
};

export default logger;
